import styled, { keyframes, css } from 'styled-components';

// Animações
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const glowPulse = keyframes`
  0% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.1); }
  50% { box-shadow: 0 0 15px rgba(64, 128, 255, 0.3); }
  100% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.1); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const dataFlow = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.7; }
`;

// Componentes estilizados
export const AuthContainer = styled.div`
  max-width: 500px;
  margin: 0 auto;
  padding: 3rem 2rem;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #fff;
  position: relative;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 10% 10%, rgba(64, 128, 255, 0.1), transparent 40%),
      radial-gradient(circle at 90% 90%, rgba(0, 200, 255, 0.1), transparent 40%);
    z-index: -1;
    pointer-events: none;
  }
`;

export const AuthHeader = styled.header`
  text-align: center;
  margin-bottom: 3rem;
  animation: ${fadeIn} 0.8s ease-out;
`;

export const AuthTitle = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  background: linear-gradient(to right, #64B6FF, #00FFFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 10px rgba(64, 128, 255, 0.3);
`;

export const AuthSubtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
`;

export const AuthCard = styled.div`
  background: rgba(15, 25, 45, 0.8);
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  border: 1px solid rgba(64, 128, 255, 0.2);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  animation: ${glowPulse} 4s infinite;
  position: relative;
  overflow: hidden;
  z-index: 1;
`;

export const TechGrid = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(64, 128, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(64, 128, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
  z-index: 0;
  pointer-events: none;
`;

export const DataStream = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 2px;
    height: 40px;
    background: linear-gradient(to bottom, transparent, rgba(64, 128, 255, 0.5), transparent);
    animation: ${dataFlow} 8s infinite;
    opacity: 0;
  }
  
  &::before {
    left: 20%;
    animation-delay: 0s;
  }
  
  &::after {
    left: 80%;
    animation-delay: 4s;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(64, 128, 255, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4C6EF5;
    box-shadow: 0 0 0 2px rgba(76, 110, 245, 0.3);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

export const Button = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(to right, #4C6EF5, #64B6FF);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    background: rgba(64, 128, 255, 0.3);
    cursor: not-allowed;
    transform: translateY(0);
    box-shadow: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

export const ErrorMessage = styled.div`
  color: #FF5252;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const SuccessMessage = styled.div`
  color: #00E676;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: ${rotate} 1s linear infinite;
  margin-right: 0.5rem;
`;

export const CodeInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin: 2rem 0;
`;

export const CodeInput = styled.input`
  width: 3rem;
  height: 3.5rem;
  text-align: center;
  font-size: 1.5rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(64, 128, 255, 0.3);
  border-radius: 8px;
  color: #fff;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4C6EF5;
    box-shadow: 0 0 0 2px rgba(76, 110, 245, 0.3);
  }
  
  &.filled {
    border-color: #00E676;
    background: rgba(0, 230, 118, 0.1);
  }
`;

export const ResendCodeButton = styled.button`
  background: transparent;
  border: none;
  color: #64B6FF;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    color: #00FFFF;
    text-decoration: underline;
  }
  
  &:disabled {
    color: rgba(100, 182, 255, 0.5);
    cursor: not-allowed;
    text-decoration: none;
  }
`;

export const TimerText = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin-left: 0.5rem;
`;

export const Logo = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 1rem;
`;

export const TechCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${props => props.size || '100%'};
  height: ${props => props.size || '100%'};
  border-radius: 50%;
  border: 1px solid ${props => props.color || 'rgba(64, 128, 255, 0.3)'};
  animation: ${pulse} 3s infinite;
  animation-delay: ${props => props.delay || 0}s;
  opacity: 0.3;
  z-index: 0;
`;

export const PasswordRequirements = styled.div`
  margin-top: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1rem;
  font-size: 0.9rem;
  
  p {
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.8);
  }
  
  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }
  
  li {
    margin-bottom: 0.3rem;
    padding-left: 1.5rem;
    position: relative;
    color: rgba(255, 255, 255, 0.6);
    transition: all 0.3s ease;
    
    &::before {
      content: '○';
      position: absolute;
      left: 0;
      color: rgba(255, 255, 255, 0.4);
      transition: all 0.3s ease;
    }
    
    &.valid {
      color: rgba(0, 230, 118, 0.9);
      
      &::before {
        content: '✓';
        color: rgba(0, 230, 118, 0.9);
        animation: ${fadeIn} 0.3s ease-out;
      }
    }
  }
`;

export const ProgressIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 2rem 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: rgba(64, 128, 255, 0.3);
    transform: translateY(-50%);
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    height: 2px;
    background: linear-gradient(to right, #4C6EF5, #64B6FF);
    transform: translateY(-50%);
    z-index: 0;
    width: ${props => {
      if (props.currentStep === 1) return '0%';
      if (props.currentStep === 2) return '33%';
      if (props.currentStep === 3) return '67%';
      if (props.currentStep === 4) return '100%';
      return '0%';
    }};
    transition: width 0.5s ease;
  }
`;

export const ProgressStep = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${props => {
    if (props.completed) return 'linear-gradient(to right, #00C853, #00E676)';
    return props.active ? 'linear-gradient(to right, #4C6EF5, #64B6FF)' : 'rgba(15, 25, 45, 0.8)';
  }};
  border: 2px solid ${props => {
    if (props.completed) return '#00E676';
    return props.active ? '#64B6FF' : 'rgba(64, 128, 255, 0.3)';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: bold;
  font-size: 0.9rem;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
  box-shadow: ${props => props.completed ? '0 0 10px rgba(0, 230, 118, 0.5)' : 'none'};
  
  &::before {
    content: ${props => props.completed ? "'✓'" : "attr(data-step)"};
    position: relative;
    font-size: ${props => props.completed ? '1.1rem' : '0.9rem'};
    animation: ${fadeIn} 0.3s ease-out;
  }
`;
